import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Users,
  Target,
  TrendingUp,
  Activity,
  Flame,
  Medal,
  Eye,
  Mail,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  Clock
} from "lucide-react";
import * as LucideIcons from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TeamView = () => {
  const [teamData, setTeamData] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [activityFeed, setActivityFeed] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userProspects, setUserProspects] = useState([]);
  const [userFollowups, setUserFollowups] = useState([]);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [sendingReminders, setSendingReminders] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [teamRes, statsRes, feedRes] = await Promise.all([
        axios.get(`${API}/team/overview`),
        axios.get(`${API}/team/stats`),
        axios.get(`${API}/team/activity-feed?limit=30`)
      ]);
      setTeamData(teamRes.data);
      setTeamStats(statsRes.data);
      setActivityFeed(feedRes.data);
    } catch (error) {
      console.error("Error fetching team data:", error);
      toast.error("Failed to load team data");
    } finally {
      setLoading(false);
    }
  };

  const viewUserDetails = async (user) => {
    setSelectedUser(user);
    setDetailDialogOpen(true);
    
    try {
      const [prospectsRes, followupsRes] = await Promise.all([
        axios.get(`${API}/team/user/${user.id}/prospects`),
        axios.get(`${API}/team/user/${user.id}/followups?limit=20`)
      ]);
      setUserProspects(prospectsRes.data);
      setUserFollowups(followupsRes.data);
    } catch (error) {
      console.error("Error fetching user details:", error);
    }
  };

  const sendOverdueReminders = async () => {
    setSendingReminders(true);
    try {
      const response = await axios.post(`${API}/notifications/send-overdue-reminders`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to send reminders");
    } finally {
      setSendingReminders(false);
    }
  };

  const getIcon = (iconName) => {
    const IconComponent = LucideIcons[iconName];
    return IconComponent ? <IconComponent className="w-4 h-4" /> : <LucideIcons.MessageSquare className="w-4 h-4" />;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return "Just now";
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 7) return `${days}d ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gba-green" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white flex items-center gap-2">
            <Users className="w-8 h-8 text-gba-green" />
            Team Dashboard
          </h1>
          <p className="text-slate-400 mt-1">
            Monitor team performance and activity
          </p>
        </div>
        <Button
          onClick={sendOverdueReminders}
          disabled={sendingReminders}
          className="bg-amber-600 hover:bg-amber-700 text-white"
          data-testid="send-reminders-button"
        >
          {sendingReminders ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Mail className="w-4 h-4 mr-2" />
          )}
          Send Overdue Reminders
        </Button>
      </div>

      {/* Team Stats */}
      {teamStats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Team Members</p>
                  <p className="font-heading text-3xl font-bold text-white mt-1">
                    {teamStats.total_users}
                  </p>
                  <p className="text-gba-green text-sm">
                    {teamStats.active_users_today} active today
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-blue-900/50 flex items-center justify-center">
                  <Users className="w-6 h-6 text-blue-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Prospects</p>
                  <p className="font-heading text-3xl font-bold text-white mt-1">
                    {teamStats.total_prospects}
                  </p>
                  <p className="text-gba-green text-sm">
                    {teamStats.conversion_rate}% converted
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-green-900/50 flex items-center justify-center">
                  <Target className="w-6 h-6 text-gba-green" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Follow-ups Today</p>
                  <p className="font-heading text-3xl font-bold text-white mt-1">
                    {teamStats.followups_today}
                  </p>
                  <p className="text-slate-400 text-sm">
                    {teamStats.followups_this_week} this week
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-purple-900/50 flex items-center justify-center">
                  <Activity className="w-6 h-6 text-purple-400" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-slate-400 text-sm">Total Overdue</p>
                  <p className="font-heading text-3xl font-bold text-white mt-1">
                    {teamStats.total_overdue}
                  </p>
                  <p className="text-red-400 text-sm">
                    Needs attention
                  </p>
                </div>
                <div className="w-12 h-12 rounded-full bg-red-900/50 flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-red-400" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs: Team Members & Activity */}
      <Tabs defaultValue="members" className="w-full">
        <TabsList className="bg-[#1E293B] border border-slate-700 p-1">
          <TabsTrigger
            value="members"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="team-members-tab"
          >
            Team Members ({teamData.length})
          </TabsTrigger>
          <TabsTrigger
            value="activity"
            className="data-[state=active]:bg-gba-green data-[state=active]:text-white"
            data-testid="activity-feed-tab"
          >
            Activity Feed
          </TabsTrigger>
        </TabsList>

        <TabsContent value="members" className="mt-4">
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="border-slate-700 hover:bg-transparent">
                    <TableHead className="text-slate-400">Name</TableHead>
                    <TableHead className="text-slate-400">Streak</TableHead>
                    <TableHead className="text-slate-400">Points</TableHead>
                    <TableHead className="text-slate-400">Prospects</TableHead>
                    <TableHead className="text-slate-400">Conversion</TableHead>
                    <TableHead className="text-slate-400">Today</TableHead>
                    <TableHead className="text-slate-400">Overdue</TableHead>
                    <TableHead className="text-slate-400 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teamData.map((member, index) => (
                    <TableRow
                      key={member.id}
                      className="border-slate-700"
                      data-testid={`team-member-row-${member.id}`}
                    >
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                            index === 0 ? "bg-amber-500 text-white" :
                            index === 1 ? "bg-slate-400 text-slate-900" :
                            index === 2 ? "bg-amber-700 text-white" :
                            "bg-gba-green/20 text-gba-green"
                          }`}>
                            {index < 3 ? index + 1 : member.name?.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <p className="text-white font-medium">{member.name}</p>
                            <p className="text-slate-500 text-xs">{member.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-orange-400">
                          <Flame className="w-4 h-4" />
                          {member.streak}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-amber-400">
                          <Medal className="w-4 h-4" />
                          {member.points}
                        </div>
                      </TableCell>
                      <TableCell className="text-white">{member.total_prospects}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={member.conversion_rate} className="w-16 h-2 bg-slate-700" />
                          <span className="text-gba-green text-sm">{member.conversion_rate}%</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          member.followups_today > 0 
                            ? "bg-gba-green/20 text-gba-green"
                            : "bg-slate-700 text-slate-400"
                        }`}>
                          {member.followups_today}
                        </span>
                      </TableCell>
                      <TableCell>
                        {member.overdue > 0 ? (
                          <span className="px-2 py-1 rounded text-xs font-medium bg-red-900/30 text-red-400">
                            {member.overdue}
                          </span>
                        ) : (
                          <CheckCircle2 className="w-4 h-4 text-gba-green" />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewUserDetails(member)}
                          className="text-slate-400 hover:text-white"
                          data-testid={`view-user-${member.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="activity" className="mt-4">
          <Card className="bg-[#1E293B] border-slate-700">
            <CardContent className="p-4">
              <ScrollArea className="h-[500px]">
                <div className="space-y-3">
                  {activityFeed.map((activity) => (
                    <div
                      key={activity.id}
                      className="flex items-start gap-3 p-3 bg-slate-800/50 rounded-lg"
                    >
                      <div className="w-10 h-10 rounded-full bg-gba-green/20 flex items-center justify-center text-gba-green flex-shrink-0">
                        {getIcon(activity.technique_icon)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white text-sm">
                          <span className="font-medium">{activity.user_name}</span>
                          {" used "}
                          <span className="text-gba-green">{activity.technique_name}</span>
                          {" with "}
                          <span className="font-medium">{activity.prospect_name}</span>
                        </p>
                        <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                          <span>{activity.channel}</span>
                          <span>{formatDate(activity.date)}</span>
                        </div>
                        {activity.notes && (
                          <p className="text-slate-400 text-xs mt-1 truncate">
                            "{activity.notes}"
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Details Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="bg-[#1E293B] border-slate-700 max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedUser && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-2xl text-white">
                  {selectedUser.name}'s Profile
                </DialogTitle>
              </DialogHeader>

              <div className="mt-4 space-y-6">
                {/* User Stats */}
                <div className="grid grid-cols-4 gap-3">
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-white">{selectedUser.total_prospects}</p>
                    <p className="text-xs text-slate-400">Prospects</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-gba-green">{selectedUser.converted}</p>
                    <p className="text-xs text-slate-400">Converted</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-400">{selectedUser.streak}</p>
                    <p className="text-xs text-slate-400">Day Streak</p>
                  </div>
                  <div className="bg-slate-800/50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-amber-400">{selectedUser.points}</p>
                    <p className="text-xs text-slate-400">Points</p>
                  </div>
                </div>

                {/* Recent Follow-ups */}
                <div>
                  <h4 className="font-medium text-white mb-3">Recent Follow-ups</h4>
                  <div className="space-y-2">
                    {userFollowups.slice(0, 5).map((f) => (
                      <div key={f.id} className="flex items-center gap-3 p-2 bg-slate-800/50 rounded-lg">
                        <div className="w-8 h-8 rounded-full bg-gba-green/20 flex items-center justify-center text-gba-green">
                          {getIcon(f.technique_icon)}
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-white">{f.technique_name}</p>
                          <p className="text-xs text-slate-400">
                            {f.prospect_name} • {f.channel}
                          </p>
                        </div>
                        <span className="text-xs text-slate-500">{formatDate(f.date)}</span>
                      </div>
                    ))}
                    {userFollowups.length === 0 && (
                      <p className="text-slate-500 text-sm">No follow-ups yet</p>
                    )}
                  </div>
                </div>

                {/* Prospects */}
                <div>
                  <h4 className="font-medium text-white mb-3">Prospects ({userProspects.length})</h4>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {userProspects.slice(0, 10).map((p) => (
                      <div key={p.id} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-lg">
                        <div>
                          <p className="text-sm text-white">{p.name}</p>
                          <p className="text-xs text-slate-400">{p.company}</p>
                        </div>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          p.status === "Converted" ? "bg-gba-green/20 text-gba-green" :
                          p.status === "Hot" ? "bg-red-900/30 text-red-400" :
                          "bg-slate-700 text-slate-300"
                        }`}>
                          {p.status}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamView;
